import { useEffect, useRef } from "react";
import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
  type QueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from "@tanstack/react-query";
import { apiDelete, apiGet, apiPost, apiPut, type ApiError } from "./http";
import type {
  BiwengerCheck,
  BiwengerConfig,
  BiwengerConfigPatch,
  DiagnosticsReport,
  LineupResult,
  MarketFilters,
  MarketResponse,
  Plan,
  PlanBody,
  PlayerDetail,
  PlayerFilters,
  PlayersResponse,
  RecommendLineupBody,
  RecommendResult,
  RecommendSquadBody,
  RecommendTransfersBody,
  Settings,
  SettingsPatch,
  SquadResponse,
  State,
  SyncKind,
  SyncRun,
  SyncStarted,
  TransfersResult,
} from "./types";

/**
 * Los datos del servidor, vistos como hooks — M7-T02 (UC-07.02).
 *
 * TODA LECTURA PASA POR AQUÍ
 *   La sección 8.2 lo pide así: cada pantalla de M8 pide su hook y recibe `isPending` para el
 *   esqueleto, `error` para el `ErrorState` y los datos ya tipados con la sección 9.2. Ninguna
 *   pantalla llama a `api/http.ts` directamente salvo el acceso, que no es una lectura.
 *
 * LAS CLAVES DE CONSULTA ESTÁN CENTRALIZADAS
 *   `queryKeys` es la lista completa. Tenerlas juntas es lo que permite invalidar con precisión
 *   —cambiar el plan solo afecta a lo que enseña el plan— y, sobre todo, no equivocarse: dos
 *   claves iguales escritas en dos sitios distintos comparten caché sin que nadie lo note.
 *
 * `keepPreviousData` EN LAS LISTAS
 *   Sección 8.2: al cambiar un filtro o un orden se siguen viendo los datos anteriores, atenuados,
 *   hasta que llegan los nuevos. Sin esto la lista parpadea a esqueleto en cada pulsación.
 *
 * EL POLLING VIVE EN `useSync` Y EN NINGÚN OTRO SITIO
 *   Una sincronización tarda minutos y toca **todas** las tablas. Mientras corre se pregunta por
 *   `GET /api/sync/runs` cada dos segundos (sección 8.2) y, en cuanto la tanda deja de estar viva,
 *   se invalida la caché entera: presupuesto, plantilla, mercado, catálogo y plan cambian a la vez.
 *
 * LOS TRES `useRecommend*`
 *   Sus cuerpos y sus respuestas son los de la sección 9.1 y 9.2. Las tres rutas ya existen
 *   (M4-T03); la comprobación manual de más abajo es anterior y por eso las recoge con un 404.
 *
 * COMPROBACIÓN MANUAL CONTRA EL SERVIDOR DE M4-T02 (aceptación de M7-T02)
 *   Hecha el 26-08-2026 contra el servidor real arrancado con una base de datos aparte y
 *   `BIWENGER_MODE=offline` (ni una petición a Biwenger: los datos entraron por
 *   `POST /api/import/:kind` desde `server/fixtures/biwenger/_capturas/`). Se pidieron las **URL
 *   exactas** que arma `buildUrl` para cada hook y se comparó la respuesta con `api/types.ts`:
 *
 *     GET  /api/state                             200 · State completo, `running: null`
 *     GET  /api/sync/runs?limit=10                200 · SyncRun[] (id, kind, status, stats…)
 *     POST /api/sync                              202 · { runId }
 *     GET  /api/squad?plan=0  ·  ?plan=1          200 · { budget, budgetAfterPlan, players }
 *     GET  /api/market?position=DEF&sort=score&plan=0
 *                                                 200 · { listings } con source/listingPrice/until
 *     GET  /api/players?sort=score&limit=3&offset=0
 *                                                 200 · { items, total }
 *     GET  /api/players/41097                     200 · { player, rounds, prices, nextFixture,
 *                                                        inSquad, listing }
 *     GET  /api/settings · PUT /api/settings      200 · Settings entero ya fusionado
 *     PUT  /api/settings (peso fuera de rango)    400 · { error, code: "validation", details }
 *     GET  /api/plan · PUT /api/plan              200 · { buys, sells, planValid, reasons }
 *     DELETE /api/plan                            204 · sin cuerpo
 *     GET  /api/state sin cookie                  401 · { error: "unauthorized" }
 *     POST /api/recommend/squad                   404 · pendiente de M4-T03, como se esperaba
 *
 *   Ni un campo de más ni de menos respecto a la sección 9.2. `fitness` llegó con números y con
 *   textos de estado mezclados, que es justo lo que declara `(number | string | null)[]`.
 */

/* ====================================================================== claves de caché === */

/** La raíz de todas las claves: `invalidateQueries()` sin argumentos las alcanza todas. */
export const QUERY_ROOT = "api";

export const queryKeys = {
  root: [QUERY_ROOT] as const,
  state: () => [QUERY_ROOT, "state"] as const,
  syncRuns: (limit: number) => [QUERY_ROOT, "sync", "runs", limit] as const,
  squad: (plan: boolean) => [QUERY_ROOT, "squad", plan] as const,
  market: (filters: MarketFilters, plan: boolean) =>
    [QUERY_ROOT, "market", filters, plan] as const,
  players: (filters: PlayerFilters) =>
    [QUERY_ROOT, "players", "list", filters] as const,
  player: (id: number) => [QUERY_ROOT, "players", "detail", id] as const,
  settings: () => [QUERY_ROOT, "settings"] as const,
  biwengerConfig: () => [QUERY_ROOT, "biwenger", "config"] as const,
  diagnostics: () => [QUERY_ROOT, "diagnostics"] as const,
  plan: () => [QUERY_ROOT, "plan"] as const,
};

/**
 * Lo que cambia cuando se toca el plan: las marcas `inPlan` del mercado y de la plantilla, los dos
 * presupuestos y el resumen de *Inicio*. El catálogo y los ajustes no dependen del plan.
 */
function invalidatePlanReaders(queryClient: QueryClient): void {
  void queryClient.invalidateQueries({ queryKey: queryKeys.plan() });
  void queryClient.invalidateQueries({ queryKey: queryKeys.state() });
  void queryClient.invalidateQueries({ queryKey: [QUERY_ROOT, "squad"] });
  void queryClient.invalidateQueries({ queryKey: [QUERY_ROOT, "market"] });
}

/* ============================================================================== estado ==== */

/** `GET /api/state`: lo que pinta *Inicio* de una sola vez (presupuesto, puntos, plan, avisos). */
export function useAppState(): UseQueryResult<State, ApiError> {
  return useQuery<State, ApiError>({
    queryKey: queryKeys.state(),
    queryFn: ({ signal }) => apiGet<State>("/state", { signal }),
  });
}

/* ============================================================================ plantilla === */

/** `GET /api/squad`. Con `plan` encendido devuelve la plantilla virtual de la sección 7.5. */
export function useSquad(
  plan = false,
): UseQueryResult<SquadResponse, ApiError> {
  return useQuery<SquadResponse, ApiError>({
    queryKey: queryKeys.squad(plan),
    queryFn: ({ signal }) =>
      apiGet<SquadResponse>("/squad", { query: { plan }, signal }),
    placeholderData: keepPreviousData,
  });
}

/* ============================================================================== mercado === */

/** `GET /api/market`: las ventas abiertas del último vistazo, filtradas y ordenadas. */
export function useMarket(
  filters: MarketFilters = {},
  plan = false,
): UseQueryResult<MarketResponse, ApiError> {
  return useQuery<MarketResponse, ApiError>({
    queryKey: queryKeys.market(filters, plan),
    queryFn: ({ signal }) =>
      apiGet<MarketResponse>("/market", {
        query: {
          position: filters.position,
          maxPrice: filters.maxPrice,
          sort: filters.sort,
          plan,
        },
        signal,
      }),
    placeholderData: keepPreviousData,
  });
}

/* ============================================================================= catálogo === */

/** `GET /api/players`: el catálogo entero, paginado. `total` es el conjunto filtrado. */
export function usePlayers(
  filters: PlayerFilters = {},
): UseQueryResult<PlayersResponse, ApiError> {
  return useQuery<PlayersResponse, ApiError>({
    queryKey: queryKeys.players(filters),
    queryFn: ({ signal }) =>
      apiGet<PlayersResponse>("/players", {
        query: {
          q: filters.q,
          position: filters.position,
          sort: filters.sort,
          limit: filters.limit,
          offset: filters.offset,
        },
        signal,
      }),
    placeholderData: keepPreviousData,
  });
}

/**
 * `GET /api/players/:id`: la ficha entera en una sola petición.
 * Con `id` nulo la consulta queda apagada, que es lo que necesita una ruta cuyo `:id` aún no se ha
 * podido leer: pedir `/api/players/NaN` sería un 404 gratuito.
 */
export function usePlayer(
  id: number | null,
): UseQueryResult<PlayerDetail, ApiError> {
  return useQuery<PlayerDetail, ApiError>({
    queryKey: queryKeys.player(id ?? 0),
    queryFn: ({ signal }) =>
      apiGet<PlayerDetail>(`/players/${String(id)}`, { signal }),
    enabled: id !== null,
  });
}

/* ============================================================================== ajustes === */

export function useSettings(): UseQueryResult<Settings, ApiError> {
  return useQuery<Settings, ApiError>({
    queryKey: queryKeys.settings(),
    queryFn: ({ signal }) => apiGet<Settings>("/settings", { signal }),
  });
}

/**
 * `PUT /api/settings` con un parcial. La respuesta son los ajustes **ya fusionados**, así que se
 * guardan tal cual en la caché; después se invalida el resto porque los pesos y las reglas de liga
 * cambian lo que valen las recomendaciones y la validez del plan.
 */
export function useUpdateSettings(): UseMutationResult<
  Settings,
  ApiError,
  SettingsPatch
> {
  const queryClient = useQueryClient();
  return useMutation<Settings, ApiError, SettingsPatch>({
    mutationFn: (patch) => apiPut<Settings>("/settings", patch),
    onSuccess: (settings) => {
      queryClient.setQueryData(queryKeys.settings(), settings);
      invalidatePlanReaders(queryClient);
    },
  });
}

/* ============================================================ conexión con Biwenger ======= */

/**
 * `GET /api/biwenger/config`: el token, la liga y el usuario con los que se habla con Biwenger.
 * Va aparte de los ajustes porque el token es un secreto y el servidor no lo devuelve nunca.
 */
export function useBiwengerConfig(): UseQueryResult<BiwengerConfig, ApiError> {
  return useQuery<BiwengerConfig, ApiError>({
    queryKey: queryKeys.biwengerConfig(),
    queryFn: ({ signal }) =>
      apiGet<BiwengerConfig>("/biwenger/config", { signal }),
  });
}

/**
 * `PUT /api/biwenger/config` con un parcial. La respuesta es la conexión ya fusionada, así que se
 * guarda tal cual en la caché.
 *
 * Después se invalida *Inicio*: pegar un token nuevo apaga el aviso de «token caducado» en el
 * servidor, y sin esto el aviso seguiría en pantalla hasta la siguiente recarga.
 */
export function useUpdateBiwengerConfig(): UseMutationResult<
  BiwengerConfig,
  ApiError,
  BiwengerConfigPatch
> {
  const queryClient = useQueryClient();
  return useMutation<BiwengerConfig, ApiError, BiwengerConfigPatch>({
    mutationFn: (patch) => apiPut<BiwengerConfig>("/biwenger/config", patch),
    onSuccess: (config) => {
      queryClient.setQueryData(queryKeys.biwengerConfig(), config);
      void queryClient.invalidateQueries({ queryKey: queryKeys.state() });
    },
  });
}

/**
 * `POST /api/biwenger/config/test`: prueba las credenciales **sin guardarlas**, fundiendo lo que
 * se le mande sobre lo que hay guardado. Así se comprueba un token antes de sustituir el que
 * funciona.
 *
 * No invalida nada: probar no cambia el estado de nadie.
 */
export function useCheckBiwengerConfig(): UseMutationResult<
  BiwengerCheck,
  ApiError,
  BiwengerConfigPatch
> {
  return useMutation<BiwengerCheck, ApiError, BiwengerConfigPatch>({
    mutationFn: (patch) =>
      apiPost<BiwengerCheck>("/biwenger/config/test", patch),
  });
}

/* ========================================================================= diagnóstico ==== */

/**
 * `GET /api/diagnostics`: el informe que se copia cuando algo falla.
 *
 * `staleTime: 0` a propósito: aquí interesa la foto de **ahora**, no una de hace medio minuto.
 * Quien abre este apartado lo hace porque algo va mal y va a copiar lo que vea.
 */
export function useDiagnostics(): UseQueryResult<DiagnosticsReport, ApiError> {
  return useQuery<DiagnosticsReport, ApiError>({
    queryKey: queryKeys.diagnostics(),
    queryFn: ({ signal }) =>
      apiGet<DiagnosticsReport>("/diagnostics", { signal }),
    staleTime: 0,
  });
}

/* ================================================================================= plan === */

export function usePlan(): UseQueryResult<Plan, ApiError> {
  return useQuery<Plan, ApiError>({
    queryKey: queryKeys.plan(),
    queryFn: ({ signal }) => apiGet<Plan>("/plan", { signal }),
  });
}

/** `PUT /api/plan`: guarda compras y ventas y devuelve el veredicto (`planValid`, `reasons`). */
export function useUpdatePlan(): UseMutationResult<Plan, ApiError, PlanBody> {
  const queryClient = useQueryClient();
  return useMutation<Plan, ApiError, PlanBody>({
    mutationFn: (plan) => apiPut<Plan>("/plan", plan),
    onSuccess: (plan) => {
      queryClient.setQueryData(queryKeys.plan(), plan);
      invalidatePlanReaders(queryClient);
    },
  });
}

/** `DELETE /api/plan`: vacía el plan. Responde `204`, así que no hay nada que guardar. */
export function useClearPlan(): UseMutationResult<void, ApiError, void> {
  const queryClient = useQueryClient();
  return useMutation<void, ApiError, void>({
    mutationFn: () => apiDelete("/plan"),
    onSuccess: () => {
      invalidatePlanReaders(queryClient);
    },
  });
}

/* ====================================================================== sincronización === */

/** Cada cuánto se pregunta por el progreso mientras hay una tanda viva (sección 8.2). */
export const SYNC_POLL_MS = 2_000;

/** Cuántas ejecuciones se traen: las mismas que devuelve la ruta si nadie pide otra cosa. */
export const SYNC_RUNS_LIMIT = 10;

/** La tanda viva entre las devueltas, o `null` si no hay ninguna. */
export function findRunningSync(runs: SyncRun[] | undefined): SyncRun | null {
  return runs?.find((run) => run.status === "running") ?? null;
}

export interface UseSyncResult {
  /** Lanza la sincronización. Sin `kind` sincroniza todo, que es lo que hace el botón. */
  sync: (kind?: SyncKind) => void;
  /** La petición de arranque está en vuelo (el 202 aún no ha vuelto). */
  isStarting: boolean;
  /** Por qué no ha arrancado: `409` con `code: "sync_running"` si ya había otra. */
  startError: ApiError | null;
  /** La tanda en marcha ahora mismo, con sus recuentos parciales. */
  running: SyncRun | null;
  /** `true` mientras haya una tanda viva: es lo que deshabilita el botón *Sincronizar*. */
  isRunning: boolean;
  /** El historial reciente, de la más nueva a la más vieja. */
  runs: SyncRun[];
  /** Fallo al leer el historial (no al arrancar la tanda). */
  runsError: ApiError | null;
  isPending: boolean;
}

/**
 * `POST /api/sync` más el seguimiento de la tanda (sección 8.2).
 *
 * CÓMO SE ENCIENDE Y SE APAGA EL POLLING
 *   `refetchInterval` mira **los datos**, no una bandera propia: mientras la lista traiga una fila
 *   `running` se vuelve a preguntar a los dos segundos, y en cuanto no la traiga devuelve `false`
 *   y el temporizador se apaga solo. Como la condición sale de la respuesta, también se engancha a
 *   una tanda que haya lanzado el cron sin que nadie pulsara el botón.
 *
 * LA INVALIDACIÓN TOTAL VA UNA SOLA VEZ
 *   El efecto se dispara en el **canto**: solo cuando había una tanda viva y deja de haberla. Sin
 *   ese testigo, cada respuesta sin tanda viva —que son casi todas— invalidaría la caché entera y
 *   la aplicación estaría recargándose sin parar.
 */
export function useSync(): UseSyncResult {
  const queryClient = useQueryClient();

  const runsQuery = useQuery<SyncRun[], ApiError>({
    queryKey: queryKeys.syncRuns(SYNC_RUNS_LIMIT),
    queryFn: ({ signal }) =>
      apiGet<SyncRun[]>("/sync/runs", {
        query: { limit: SYNC_RUNS_LIMIT },
        signal,
      }),
    refetchInterval: (query) =>
      findRunningSync(query.state.data) === null ? false : SYNC_POLL_MS,
    // El progreso es lo único que no admite los 30 s de frescura general: se enseña en vivo.
    staleTime: 0,
  });

  const running = findRunningSync(runsQuery.data);
  const runningId = running?.id ?? null;
  const habiaTanda = useRef(false);

  useEffect(() => {
    if (runningId !== null) {
      habiaTanda.current = true;
      return;
    }
    if (!habiaTanda.current) return;
    habiaTanda.current = false;
    // Una tanda toca todas las tablas: no hay nada en la caché que siga siendo de fiar.
    void queryClient.invalidateQueries();
  }, [runningId, queryClient]);

  const mutation = useMutation<SyncStarted, ApiError, SyncKind | undefined>({
    mutationFn: (kind) =>
      apiPost<SyncStarted>("/sync", { kind: kind ?? "all" }),
    onSuccess: () => {
      // El 202 solo dice que ha arrancado: quien enseña el progreso es el historial.
      void queryClient.invalidateQueries({
        queryKey: queryKeys.syncRuns(SYNC_RUNS_LIMIT),
      });
    },
  });

  return {
    sync: (kind) => {
      mutation.mutate(kind);
    },
    isStarting: mutation.isPending,
    startError: mutation.error,
    running,
    isRunning: running !== null,
    runs: runsQuery.data ?? [],
    runsError: runsQuery.error,
    isPending: runsQuery.isPending,
  };
}

/* ===================================================================== recomendaciones === */

/** `POST /api/recommend/squad`: la plantilla objetivo y los movimientos para llegar a ella. */
export function useRecommendSquad(): UseMutationResult<
  RecommendResult,
  ApiError,
  RecommendSquadBody
> {
  return useMutation<RecommendResult, ApiError, RecommendSquadBody>({
    mutationFn: (body) => apiPost<RecommendResult>("/recommend/squad", body),
  });
}

/** `POST /api/recommend/transfers`: los fichajes elegidos y sus alternativas. */
export function useRecommendTransfers(): UseMutationResult<
  TransfersResult,
  ApiError,
  RecommendTransfersBody
> {
  return useMutation<TransfersResult, ApiError, RecommendTransfersBody>({
    mutationFn: (body) =>
      apiPost<TransfersResult>("/recommend/transfers", body),
  });
}

/** `POST /api/recommend/lineup`: el once y el banquillo de la próxima jornada. */
export function useRecommendLineup(): UseMutationResult<
  LineupResult,
  ApiError,
  RecommendLineupBody
> {
  return useMutation<LineupResult, ApiError, RecommendLineupBody>({
    mutationFn: (body) => apiPost<LineupResult>("/recommend/lineup", body),
  });
}
